
"use client"; 

import type { Metadata } from 'next'; 
import Link from 'next/link';
import Image from 'next/image'; 
import Script from 'next/script'; 
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Header } from '@/components/layout/Header';
import { Lightbulb, MessageSquareText } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext'; 
import { PRICING_TIERS } from '@/lib/constants';

const developerName = "Ganesh S P";

export default function HomePage() {
  const { user, loading: authLoading, isEffectivelyPremium, currentTier } = useAuth(); 
  
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">
        <section className="w-full py-12 md:py-24 lg:py-32 xl:py-48">
          <div className="container px-4 md:px-6">
            <div className="grid gap-6 lg:grid-cols-[1fr_400px] lg:gap-12 xl:grid-cols-[1fr_600px]">
              <div className="flex flex-col justify-center space-y-4">
                <div className="space-y-2">
                  <h1 className="text-3xl font-bold tracking-tighter sm:text-5xl xl:text-6xl/none">
                    Elevate Your Interview Skills with <span className="text-primary">IntervuAI</span>
                  </h1>
                  <p className="max-w-[600px] text-foreground/80 md:text-xl">
                    Generate custom system design questions, practice with a timer, get AI-powered feedback and grading, and visualize your ideas with our integrated whiteboard.
                  </p>
                </div>
                <div className="flex flex-col gap-2 min-[400px]:flex-row">
                  <Button asChild size="lg">
                    <Link href="/practice">Start Practicing</Link>
                  </Button>
                </div>
              </div>
              <Image
                src="/homepage.png" 
                alt="Illustration of a person at a computer practicing for a system design interview with AI assistance"
                data-ai-hint="interview tech"
                width={600}
                height={600}
                className="mx-auto aspect-video overflow-hidden rounded-xl object-cover sm:w-full lg:order-last lg:aspect-square"
                priority 
              />
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-24 lg:py-32 bg-muted">
          <div className="container px-4 md:px-6">
            <div className="flex flex-col items-center justify-center space-y-4 text-center">
              <div className="space-y-2">
                <div className="inline-block rounded-lg bg-muted-foreground/10 px-3 py-1 text-sm text-primary">Key Features</div>
                <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl">Everything you need to ace your interview</h2>
                <p className="max-w-[900px] text-foreground/80 md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed">
                  IntervuAI offers a comprehensive suite of tools designed to help you master system design concepts and interview techniques.
                </p>
              </div>
            </div>
            <div className="mx-auto grid max-w-5xl items-start gap-8 sm:grid-cols-1 md:gap-12 lg:grid-cols-2 lg:gap-16 mt-12">
              <Card className="shadow-lg hover:shadow-primary/20 transition-shadow">
                <CardHeader className="pb-4">
                  <Lightbulb className="h-8 w-8 text-primary mb-2" />
                  <CardTitle>AI Question Generation</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>Tailor questions by topic, difficulty, and scale to focus your practice.</CardDescription>
                </CardContent>
              </Card>
              <Card className="shadow-lg hover:shadow-primary/20 transition-shadow">
                <CardHeader className="pb-4">
                  <MessageSquareText className="h-8 w-8 text-primary mb-2" />
                  <CardTitle>AI Interview & Feedback</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>Engage in a conversational interview and receive detailed feedback on your performance.</CardDescription>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
